import {
  type ChatBskyActorDefs,
  type ChatBskyGroupListJoinRequests,
} from '@atproto/api'
import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import {DM_SERVICE_HEADERS} from '#/lib/constants'
import {logger} from '#/logger'
import {listConvoMembersQueryKey} from '#/state/queries/messages/list-convo-members'
import {createListJoinRequestsQueryKey} from '#/state/queries/messages/list-join-requests'
import {useAgent} from '#/state/session'

type JoinRequestAction = 'approve' | 'reject'

/** One page of the join-request list, as the query returns it. */
type JoinRequestsPage = ChatBskyGroupListJoinRequests.OutputSchema
type JoinRequestsData = InfiniteData<JoinRequestsPage>
type ConvoMember = ChatBskyActorDefs.ProfileViewBasic

export function useJoinRequestMutation<A extends JoinRequestAction>(
  action: A,
  convoId: string | undefined,
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (data: unknown) => void
    onError?: (error: Error) => void
  } = {},
) {
  const queryClient = useQueryClient()
  const agent = useAgent()

  return useMutation({
    mutationFn: async ({member}: {member: string}) => {
      if (!convoId) throw new Error('No convoId provided')
      if (action === 'approve') {
        const {data} = await agent.chat.bsky.group.approveJoinRequest(
          {convoId, member},
          {headers: DM_SERVICE_HEADERS, encoding: 'application/json'},
        )
        return data
      } else {
        const {data} = await agent.chat.bsky.group.rejectJoinRequest(
          {convoId, member},
          {headers: DM_SERVICE_HEADERS, encoding: 'application/json'},
        )
        return data
      }
    },
    onMutate: ({member}) => {
      if (!convoId) return

      const requestsKey = createListJoinRequestsQueryKey({convoId})
      const prevRequests =
        queryClient.getQueryData<JoinRequestsData>(requestsKey)

      const requestedByProfile = prevRequests?.pages
        .flatMap(page => page.requests)
        .find(request => request.requestedBy.did === member)?.requestedBy

      queryClient.setQueryData<JoinRequestsData>(requestsKey, prev => {
        if (!prev?.pages) return prev
        return {
          ...prev,
          pages: prev.pages.map(page => ({
            ...page,
            requests: page.requests.filter(
              request => request.requestedBy.did !== member,
            ),
          })),
        }
      })

      let prevMembers: ConvoMember[] | undefined
      if (action === 'approve' && requestedByProfile) {
        const membersKey = listConvoMembersQueryKey(convoId)
        prevMembers = queryClient.getQueryData<ConvoMember[]>(membersKey)
        queryClient.setQueryData<ConvoMember[]>(membersKey, prev => {
          if (!prev) return prev
          if (prev.some(m => m.did === member)) return prev
          return [...prev, requestedByProfile]
        })
      }

      return {prevRequests, prevMembers}
    },
    onSuccess: data => {
      if (convoId) {
        void queryClient.invalidateQueries({
          queryKey: createListJoinRequestsQueryKey({convoId}),
        })
        if (action === 'approve') {
          void queryClient.invalidateQueries({
            queryKey: listConvoMembersQueryKey(convoId),
          })
        }
      }
      onSuccess?.(data)
    },
    onError: (error, _variables, context) => {
      logger.error(error)
      if (convoId && context?.prevRequests) {
        queryClient.setQueryData(
          createListJoinRequestsQueryKey({convoId}),
          context.prevRequests,
        )
      }
      if (convoId && action === 'approve' && context?.prevMembers) {
        queryClient.setQueryData(
          listConvoMembersQueryKey(convoId),
          context.prevMembers,
        )
      }
      onError?.(error)
    },
  })
}
